/**
 * Common utility functions used by composition components
 */
import * as React from "react"
import * as styledComponents from "styled-components"
import { curry } from "ramda"

/**
 * Export standalone for use in components which compose sets of children such as Tabs,
 * in these cases a shared postfix makes things easier on humans doing troubleshooting
 */
const generateInternalId = () =>
  Math.random()
    .toString(36)
    .slice(2)

/**
 * Safe get for component name, prefers DisplayName when available always returns a string
 * DisplayName allows you to get the name of a HOC wrapped component
 * however, displayName is not always defined, hence fallback logic
 * @param Component
 */
const getComponentDisplayName = (
  Component:
    | React.ComponentType
    | React.ElementType
    | styledComponents.StyledFunction<any>
    | string
): string => {
  return typeof Component === "string"
    ? String(Component).length > 0
      ? Component
      : "Unknown"
    : (Component as React.ComponentType).displayName ?? Component.name
}

/**
 * Given a react element, compares its type to component
 * A second argument is available for use as a a fall-back,
 * this needed for dev environments like .docz which proxy the component
 */
const isElementOfType = curry(
  (
    type: React.ElementType,
    displayName: string,
    element: React.ReactElement
  ) => {
    return (
      element.type === type ||
      getComponentDisplayName(element.type) === displayName
    )
  }
)

export { getComponentDisplayName, generateInternalId, isElementOfType }
